import { useResource } from "hooks/useResource";
import { getMarquee } from "services/content";

/**
 * Scrolling notice strip under the hero — the running "একাদশ ভর্তি চলছে" line
 * the school asked for.
 *
 * The text is admin content, not code: staff add, reorder and remove lines in
 * Admin → Notice Ticker, switch the whole strip off, and set how long one loop
 * takes. With nothing configured it renders nothing at all rather than an empty
 * green band. The lines are not clickable — see MarqueeItem.
 *
 * The items are rendered twice. The animation moves the track by exactly half
 * its width, so the second copy is in the first one's place when the loop
 * restarts and the seam is invisible. Motion behaviour lives in index.css
 * (.mq / .mq-track): paused on hover and on focus, and switched off entirely
 * under prefers-reduced-motion.
 */

const ITEM_CLS =
  "inline-flex items-center gap-3 px-6 py-2 text-[15px] text-white/90 no-underline whitespace-nowrap";

// Lines are plain text for now, by request — a notice sliding past is an
// awkward click target anyway. Any `href` already stored is kept and simply
// not rendered, so switching links back on is a small change here.
function MarqueeItem({ item }) {
  return (
    <span className={ITEM_CLS}>
      <span className="text-gold" aria-hidden="true">
        ◆
      </span>
      <span className="font-bn">{item.text}</span>
    </span>
  );
}

export default function NoticeMarquee() {
  const { data } = useResource(getMarquee);

  const items = (data?.items || []).filter((i) => (i?.text || "").trim());
  if (data?.enabled === false || !items.length) return null;

  // Longer messages need a proportionally longer loop or they race past.
  const seconds = Number(data?.speed) > 0 ? Number(data.speed) : 30;

  return (
    <div
      className="mq relative z-[3] w-full overflow-hidden bg-green-dark border-y border-white/10"
      role="region"
      aria-label="Notices"
    >
      <div className="mq-track" style={{ "--mq-duration": `${seconds}s` }}>
        {/* The first copy is the readable one. */}
        <div className="flex items-center shrink-0">
          {items.map((item, i) => (
            <MarqueeItem key={`a${i}`} item={item} />
          ))}
        </div>
        {/* The second exists only to fill the gap as the first scrolls away,
            so it is hidden from assistive technology. */}
        <div className="flex items-center shrink-0" aria-hidden="true">
          {items.map((item, i) => (
            <MarqueeItem key={`b${i}`} item={item} />
          ))}
        </div>
      </div>
    </div>
  );
}
